
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import { getModule } from 'vuex-module-decorators';
import store from '@/store';
import SessionsModule from '@/store/modules/Sessions';
import UserModule from '@/store/modules/User';
import PhotosModule from '@/store/modules/Photos';
import SessionListElement from '@/model/SessionListElement';

const sessionsModule = getModule(SessionsModule, store);
const userModule = getModule(UserModule, store);
const photosModule = getModule(PhotosModule, store);

@Component
export default class NewFolderModal extends Vue {
    @Prop({ type: Boolean, default: false })
    public value!: boolean;

    public folderName: string = '';
    public selectedSessionIds: string[] = [];
    public creating: boolean = false;
    public showNameError: boolean = false;
    public showSessionsError: boolean = false;

    get dialog() {
        return this.value;
    }

    set dialog(value: boolean) {
        if (!value) {
            this.close();
        }
    }

    get availableSessions(): SessionListElement[] {
        if (!userModule.user) {
            return [];
        }

        // Get sessions that are not in any folder
        const userSessions = sessionsModule.sessionsNotInFolders[userModule.user.id] || [];
        return userSessions;
    }

    get canCreate(): boolean {
        return this.folderName.trim().length > 0 && this.selectedSessionIds.length > 0;
    }

    public getSessionThumbnail(session: SessionListElement): string | undefined {
        const sessionPhotosList = photosModule.sessionPhotos[session.id];
        const sessionThumbnail = session.thumbnail;
        
        if (
            sessionPhotosList &&
            sessionPhotosList.length > 0 &&
            sessionThumbnail &&
            sessionThumbnail.photoId &&
            sessionThumbnail.photoId.trim() !== '' &&
            sessionPhotosList.some(({ id }) => id === sessionThumbnail.photoId)
        ) {
            return sessionPhotosList
                .find(({ id }) => id === sessionThumbnail.photoId)!
                .smallThumbUrl;
        } else if (sessionThumbnail && sessionThumbnail.url && sessionThumbnail.url.trim() !== '') {
            return sessionThumbnail.url;
        } else if (sessionPhotosList && sessionPhotosList.length > 0) {
            return sessionPhotosList[0].smallThumbUrl;
        }
        
        return undefined;
    }

    @Watch('value')
    public onDialogChange(newValue: boolean) {
        if (newValue) {
            this.resetForm();
        }
    }

    public async createFolder() {
        if (!this.folderName.trim()) {
            this.showNameError = true;
            return;
        }

        if (!this.selectedSessionIds.length) {
            this.showSessionsError = true;
            return;
        }

        if (!this.canCreate || !userModule.user) {
            return;
        }

        this.creating = true;
        this.$emit('folder-processing', true);
        await this.$nextTick();

        try {
            await sessionsModule.createFolder({
                userId: userModule.user.id,
                folderName: this.folderName.trim(),
                sessionIds: this.selectedSessionIds,
            });

            this.$emit('folder-created');
            this.close();
        } catch (error) {
            // tslint:disable-next-line:no-console
            console.error('Error creating folder:', error);
            // You might want to show a toast notification here
        } finally {
            this.creating = false;
            this.$emit('folder-processing', false);
        }
    }

    public close() {
        this.$emit('input', false);
        this.resetForm();
    }

    public handleEnterKey() {
        if (!this.creating) {
            this.createFolder();
        }
    }

    private resetForm() {
        this.folderName = '';
        this.selectedSessionIds = [];
        this.creating = false;
        this.showNameError = false;
        this.showSessionsError = false;
    }

    public toggleSessionSelection(sessionId: string) {
        if (this.creating) {
            return;
        }
        const index = this.selectedSessionIds.indexOf(sessionId);
        if (index > -1) {
            this.selectedSessionIds.splice(index, 1);
        } else {
            this.selectedSessionIds.push(sessionId);
        }
    }
}
